/**
 * Physical-key normalisation for keyboard shortcuts.
 *
 * THE macOS PROBLEM. Option (alt) is a dead-key modifier on macOS: it does not
 * decorate a letter, it produces a DIFFERENT character. Option+V is `√`,
 * Option+C is `ç`, Option+N is `˜`. So `event.key` for Option+V is "√", and a
 * shortcut table keyed on "alt+v" can never match — in Chrome, in Safari, and
 * in the Electron shell alike.
 *
 * Every `alt+<letter>` shortcut in Tank was therefore dead on macOS, silently.
 * Found 10-08-2026 when alt+V (cycle viz layers) did nothing in the desktop
 * app; it did nothing in the browser either, which is what identified it as a
 * long-standing bug rather than an Electron regression.
 *
 * WHY `code` IS ONLY A FALLBACK, NOT THE RULE.
 *
 * `event.code` is the PHYSICAL key: the position, not the label. On a German
 * QWERTZ keyboard the key labelled Z sits at `KeyY`; on AZERTY the key labelled
 * A is `KeyQ`. Windows and Linux do NOT have the dead-key problem — Alt+Z there
 * reports `key: "z"` correctly — so preferring `code` whenever alt is held
 * would take a working Windows shortcut and match it by position instead of by
 * label, turning Alt+Z into "y" for every QWERTZ user. That is a regression
 * introduced while fixing macOS.
 *
 * So: trust `event.key` whenever it produced a usable character, and fall back
 * to `event.code` only when it did not — which is exactly the macOS dead-key
 * case and nothing else. Windows and Linux never reach the fallback.
 */

/** A single ASCII letter or digit is a usable shortcut key as typed. */
const USABLE = /^[a-z0-9]$/i;

/**
 * The letter/digit a shortcut table should be keyed on, lowercase.
 *
 * @param {KeyboardEvent} event
 * @returns {string}
 */
export function shortcutKey(event) {
  const key = typeof event.key === 'string' ? event.key : '';

  // Named keys (Escape, ArrowLeft, Enter) and ordinary characters pass
  // straight through. This is every press on Windows and Linux, and every
  // unmodified press on macOS.
  if (!event.altKey || USABLE.test(key) || key.length !== 1) {
    return key.toLowerCase();
  }

  // Alt produced a single character that is not a letter or digit: the macOS
  // dead-key case. Recover the physical key.
  const code = typeof event.code === 'string' ? event.code : '';
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) return letter[1].toLowerCase();
  const digit = /^Digit([0-9])$/.exec(code);
  if (digit) return digit[1];

  return key.toLowerCase();
}
